import { Typography } from '@mui/material';
import { type AxiosError } from 'axios';
import { useCallback, useState } from 'react';

import { DataGrid } from '@hapvida/hapvida-core-components';

import { BillTypeEnum } from '@flows/private/financial/constants/billtypeEnum';

import { useBillData, useBillState } from '../../hooks';
import { AlertReport } from '../AlertReport';
import { ButtonLink, OperationalCostModal } from './components';
import { ReportRedirectModal } from './components/ReportRedirectModal';
import { REMITTANCE_MIGRATION } from './constants';
import { useGridRows } from './helpers';
import { getReportFileRowsLessMigration } from './helpers/getReportFileRowsLessMigration';
import type { BillDetailsCardProps } from './types';

export function BillDetailsCard({ bill }: Readonly<BillDetailsCardProps>) {
  const { commonData } = useBillData(bill);
  const { fetchReportWithFileType, invoiceState } = useBillState();

  const [openOperationalCostModalState, setOpenOperationalCostModalState] =
    useState(false);
  const [openCarnetOrReportModalState, setOpenCarnetOrReportModalState] =
    useState(false);
  const [openReportMonthlyModalState, setOpenReportMonthlyModalState] =
    useState(false);

  const toggleOperationalModal = useCallback(
    () => setOpenOperationalCostModalState(!openOperationalCostModalState),
    [openOperationalCostModalState],
  );
  const toggleReportMonthlyModal = useCallback(
    () => setOpenReportMonthlyModalState(!openReportMonthlyModalState),
    [openReportMonthlyModalState],
  );
  const toggleCarnetOrReportModal = useCallback(
    () => setOpenCarnetOrReportModalState(!openCarnetOrReportModalState),
    [openCarnetOrReportModalState],
  );

  const { billType, billingNumber } = bill;

  const pdfReportState = fetchReportWithFileType('PDF', billingNumber);
  const csvReportState = fetchReportWithFileType('CSV', billingNumber);
  const txtReportState = fetchReportWithFileType('TXT', billingNumber);

  const isAnyReportMigration = [
    pdfReportState,
    csvReportState,
    txtReportState,
  ].some(({ failureReason }) => {
    return (failureReason as AxiosError)?.response?.status === 403;
  });

  const isRemittanceMigration =
    String(bill?.remittence) === REMITTANCE_MIGRATION;

  const isRemittanceOrAnyReportMigration =
    isRemittanceMigration || isAnyReportMigration;

  const isOperationalCost = billType === BillTypeEnum.OPERATIONAL_COST;
  const isCarnetComplementOrExtraInvoice =
    billType === BillTypeEnum.CARNET_COMPLEMENT ||
    billType === BillTypeEnum.EXTRA_INVOICE;

  const operationalCostRow = [
    {
      label: 'Relatório',
      xs: 3,
      value: isRemittanceMigration ? (
        <Typography variant="body2">-</Typography>
      ) : (
        <ButtonLink
          onClick={toggleOperationalModal}
          text="Acessar relatório"
          iconName="navigation-b2b-link"
        />
      ),
    },
    ...(isRemittanceMigration
      ? [
          {
            label: '',
            xs: 12,
            value: (
              <AlertReport severity="info">
                <Typography variant="text" sx={{ whiteSpace: 'pre-line' }}>
                  Os relatórios não estão disponíveis para faturas anteriores ao
                  mês de dezembro de 2024.
                </Typography>
              </AlertReport>
            ),
          },
        ]
      : []),
  ];

  const isAnyReportProcessing = [
    pdfReportState,
    csvReportState,
    txtReportState,
  ].some(report => report.data?.statusCode === 202);

  const reportMonthlyFilesRows = [
    {
      label: 'Relatório analítico (PDF)',
      xs: 3,
      value: getReportFileRowsLessMigration({
        bill,
        fileType: 'PDF',
        fetchState: pdfReportState,
      }),
    },
    {
      label: 'Relatório analítico (CSV)',
      xs: 3,
      value: getReportFileRowsLessMigration({
        bill,
        fileType: 'CSV',
        fetchState: csvReportState,
      }),
    },
    {
      label: 'Relatório analítico (TXT)',
      xs: 3,
      value: getReportFileRowsLessMigration({
        bill,
        fileType: 'TXT',
        fetchState: txtReportState,
      }),
    },

    ...(isRemittanceOrAnyReportMigration
      ? [
          {
            label: '',
            xs: 12,
            value: (
              <AlertReport severity="info">
                <Typography variant="text" sx={{ whiteSpace: 'pre-line' }}>
                  Os relatórios não estão disponíveis para faturas anteriores ao
                  mês de dezembro de 2024.
                </Typography>
              </AlertReport>
            ),
          },
        ]
      : []),

    ...(isAnyReportProcessing
      ? [
          {
            label: '',
            xs: 12,
            value: (
              <AlertReport severity="info">
                <Typography variant="text" sx={{ whiteSpace: 'pre-line' }}>
                  Prezado cliente, após a solicitação do relatório, o tempo
                  médio para disponibilização dos arquivos para download é de
                  até 24h.
                </Typography>
              </AlertReport>
            ),
          },
        ]
      : []),
  ];

  const carnetOrReportRows = isCarnetComplementOrExtraInvoice
    ? []
    : reportMonthlyFilesRows;

  const newVisibleRows = isOperationalCost
    ? operationalCostRow
    : carnetOrReportRows;

  const gridRows = useGridRows(bill, newVisibleRows, invoiceState);

  return (
    <>
      <DataGrid
        rows={[...commonData, ...gridRows]}
        gridContainerProps={{ rowSpacing: 3 }}
      />
      <OperationalCostModal
        openModal={openOperationalCostModalState}
        toggleModal={toggleOperationalModal}
      />
      <ReportRedirectModal
        title="Relatório mensal"
        openModal={openReportMonthlyModalState}
        toggleModal={toggleReportMonthlyModal}
      />
      <ReportRedirectModal
        title="Relatório Carnet Complementar-coparticipação"
        openModal={openCarnetOrReportModalState}
        toggleModal={toggleCarnetOrReportModal}
      />
    </>
  );
}
